<template>
  <div>
    <SpreadSheetLoader v-model="loadingData" />
    <div ref="container" class="widget-box" style="height: 100%" />
    <div style="position: fixed"
         :style="{ top: selectedCell.position.cellTopPosition + 'px', left: selectedCell.position.cellLeftPosition + 'px' }">
      <InlineEditor :field-type="selectedCell.type"
                    :isEditButtonShown="false"
                    :isEditingFromTable="isEditingFromTable"
                    :options="selectedCell.options"
                    option-label="name"
                    option-value="name"
                    :value="selectedCell.value"
                    @menuClosed="clearSelectedCell()"
                    @onSave="spreadsheet.setValue(selectedCell.cell, $event)" />
    </div>
  </div>
</template>

<script>
import { Spreadsheet } from '@/plugins/spreadsheet_3.1.4_commercial'
import SpreadSheetLoader from '@/components/Spreadsheet/SpreadSheetLoader'
import '@/plugins/spreadsheet_3.1.4_commercial/codebase/spreadsheet.css'
import { mapState } from 'vuex'
export default {
  components: {
    SpreadSheetLoader
  },
  computed: {
    ...mapState({
      isCloudTask: state => state.auth.isCloudTask
    }),
    filteredHeaders () {
      return [...this.headers].filter(item => !this.notDesiredHeaders.includes(item.value))
    },
    tableColumns () {
      // StackOverflow solution https://stackoverflow.com/questions/23284176/excel-like-headers-in-javascript
      const columnsArray = []
      for (let i = 0; i <= this.filteredHeaders.length; i++) {
        columnsArray.push(this.toExcelHeader(i + 1))
      }
      return columnsArray
    },
    spreadSheetData () {
      const formattedHeaders = this.filteredHeaders.map((item, index) => (
        { cell: `${this.tableColumns[index]}1`, value: item.text, css: 'headersClass' }
      ))
      const formattedData = []
      const data = [...this.items]
      for (let i = 0; i < data.length; i++) {
        for (let j = 0; j < this.filteredHeaders.length; j++) {
          const key = this.filteredHeaders[j].value
          const type = this.filteredHeaders[j].type
          let value = data[i][key]
          const replacementCountryCode = this.isCloudTask ? '1' : '57'
          if (!value && type === 'phone') {
            value = `+(${replacementCountryCode})`
          } else if (key === 'phone') {
            const { phone, countryCode } = data[i].phoneObj
            value = `+(${countryCode || replacementCountryCode})${phone}`
          } else if (type === 'phone') {
            const phoneObject = JSON.parse(data[i][key])
            const { number, phone } = phoneObject
            value = `+(${number || replacementCountryCode})${phone}`
          }
          formattedData.push({ cell: `${this.tableColumns[j]}${i + 2}`, value })
        }
      }
      return {
        data: [...formattedHeaders, ...formattedData],
        styles: {
          headersClass: {
            background: '#8598ac',
            color: '#FFFFFF'
          }
        }
      }
    }
  },
  data () {
    return {
      isEditingFromTable: false,
      selectedCell: {
        cell: '',
        element: null,
        header: {},
        options: [],
        position: { cellTopPosition: 0, cellLeftPosition: 0 },
        type: '',
        userIndex: '',
        value: ''
      },
      spreadsheet: null
    }
  },
  methods: {
    beforeEditStart (cell) {
      if (this.isEditingFromTable) return false
      const column = cell.match(/[a-zA-Z]+/g)[0]
      const rowIndex = parseInt(cell.match(/\d+/g)[0])
      const headerValue = this.spreadsheet.getValue(`${column}1`)
      if (!headerValue || rowIndex === 1) return false
      const columnIndex = parseInt(this.tableColumns.indexOf(column.toLowerCase()))
      const header = this.filteredHeaders[columnIndex]
      const { options, type } = header
      if (type === 'select' || type === 'radio' || type === 'checkbox' || type === 'money' || type === 'date') {
        const value = this.spreadsheet.getValue(cell)
        const cellElement = document.querySelector(`[dhx_grid_row="${rowIndex}"]`).childNodes[columnIndex + 1]
        const cellTopPosition = window.scrollY + cellElement.getBoundingClientRect().top
        const cellLeftPosition = window.scrollX + cellElement.getBoundingClientRect().left
        this.setSelectedCell({ cell, element: cellElement, position: { cellTopPosition, cellLeftPosition }, header, options, type, userIndex: rowIndex - 2, value })
        return false
      }
    },
    beforeFocusSet () {
      return !this.isEditingFromTable
    },
    beforeValueChange (cell, value) {
      const column = cell.match(/[a-zA-Z]+/g)[0]
      const rowIndex = parseInt(cell.match(/\d+/g)[0])
      const headerValue = this.spreadsheet.getValue(`${column}1`)
      if (!headerValue || rowIndex === 1) return false
      const columnIndex = this.tableColumns.indexOf(column.toLowerCase())
      const header = this.filteredHeaders[columnIndex]
      let isValueValid = true
      for (const rule of this.filteredHeaders[columnIndex].rules) {
        if (rule(value) !== true) {
          isValueValid = false
          break
        }
      }
      if (isValueValid) this.updateItemInline(value, rowIndex, header)
      // Returns true to allow value change and false to deny it
      return isValueValid
    },
    clearSelectedCell () {
      this.selectedCell = {
        cell: '',
        element: null,
        header: '',
        options: [],
        position: { cellTopPosition: 0, cellLeftPosition: 0 },
        type: '',
        userIndex: '',
        value: ''
      }
      this.isEditingFromTable = false
    },
    setSelectedCell (selectedCell) {
      const { ...all } = selectedCell
      this.selectedCell = { ...all }
      this.isEditingFromTable = true
    },
    toExcelHeader (index) {
      // StackOverflow solution https://stackoverflow.com/questions/23284176/excel-like-headers-in-javascript
      const charCodeOfA = ('a').charCodeAt(0)
      const charCodeOfZ = ('z').charCodeAt(0)
      let excelStr = ''
      const base24Str = (index).toString(charCodeOfZ - charCodeOfA + 1)
      for (let base24StrIndex = 0; base24StrIndex < base24Str.length; base24StrIndex++) {
        const base24Char = base24Str[base24StrIndex]
        let alphabetIndex = Number(base24Char * 1) === Number(base24Char) ? base24Char : base24Char.charCodeAt(0) - charCodeOfA + 10
        // bizarre thing, A==1 in first digit, A==0 in other digits
        if (base24StrIndex === 0) {
          alphabetIndex -= 1
        }
        excelStr += String.fromCharCode(charCodeOfA + alphabetIndex)
      }
      return excelStr.toLowerCase()
    },
    setData () {
      try {
        if (this.spreadsheet) {
          this.spreadsheet.parse(this.spreadSheetData)
        }
      } catch (e) {
        console.error(e, 'Error in spreadsheet')
      }
    },
    updateItemInline (value, rowIndex, header) {
      if (this.selectedCell.value !== value) {
        this.$emit('updateItemInline', { value, rowIndex: rowIndex - 2, header })
      }
    }
  },
  mounted () {
    // Singleton design pattern to avoid duplicated instance
    if (!this.spreadsheet) {
      this.spreadsheet = new Spreadsheet(this.$refs.container, {
        autoFormat: false,
        toolbar: this.toolbar,
        editLine: this.editLine,
        menu: this.menu
      })
      this.spreadsheet.events.on('beforeEditStart', (cell) => this.beforeEditStart(cell))
      this.spreadsheet.events.on('beforeFocusSet', (cell) => this.beforeFocusSet(cell))
      this.spreadsheet.events.on('beforeValueChange', (cell, value) => this.beforeValueChange(cell, value))
    }
  },
  props: {
    headers: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    editLine: { type: Boolean, default: true },
    loadingData: { type: Boolean, default: false },
    menu: Boolean,
    notDesiredHeaders: { type: Array, default: () => [] },
    toolbar: Array
  },
  watch: {
    items () {
      this.setData()
    }
  }
}
</script>
